import React, { useEffect, useState } from "react"
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { QuantityInputWithActions } from "../../common/inputs/QuantityInputWithActions"
import { BasicRow } from "../../common/listing/ListingTypes"
import ListingWrapper from "../../common/listing/ListingWrapper"
import { ProductInventoryQuantitiesProps } from "../../common/types/inventory"
import IntlProps from "../../state/helpers/IntlProps"
import { InventoryStockDocumentDefinitionAction, InventoryStockDocumentDefinitionSelector } from "../../state/inventory"
import { NormalizedLocation } from "../../state/inventory/location/reducers"
import { getStore } from "../../state/product/ProductSelectors"
import { ItemAdjustment, PRODUCT_TYPE } from "../../state/product/Types"
import { ProductAction } from "../../state/product/actions"
import { ProductSelector } from "../../state/product/selectors"
import { INVENTORY_IN_TRANSIT, INVENTORY_RESERVATION, INVENTORY_STOCK_DIRECTION } from "../../utils/inventory"
import { getProductQuantities } from "../../utils/inventory/utils"
import ProductStockQuantitiesModal from "../modals/ProductStockQuantitiesModal"
import Icon from "../../common/icon/icon.component"
import { ReorderPointModal } from "../modals"
import { InventoryLocationQuantities } from "../../state/inventory/data/model/inventoryData"
import { Button } from "react-bootstrap"

const ProductInventoryQuantitiesListing = (props: ProductInventoryQuantitiesProps & IntlProps) => {
    const [productInvLocationQuantities, setProductInvLocationQuantities] = useState(props.productQuantities)
    const dispatch = useDispatch()
    const [modal, setModal] = useState(<></>)
    const definitionListing = useSelector(InventoryStockDocumentDefinitionSelector.getListing)
    const productQuantities = useSelector(ProductSelector.getInventoryLocationQuantities)
    const item = useSelector(getStore)

    useEffect(() => {
        dispatch(InventoryStockDocumentDefinitionAction.fetchListing())
    }, [])

    useEffect(() => {
        if (item.type != PRODUCT_TYPE.MATRIX_PARENT) {
            setProductInvLocationQuantities(productQuantities)
        } else {
            const itemQuantities = getProductQuantities(item)
            if (itemQuantities.length > 0) {
                if (props.variantIds.length == 0) {
                    const variantQty = itemQuantities.filter(i => i.attributes.variantId === props.product.id)
                    setProductInvLocationQuantities(variantQty)
                } else {
                    setProductInvLocationQuantities(itemQuantities)
                }
            }
        }
    }, [productQuantities])

    const onModalOpen = (productId, inventoryLocationId, selectedModal) => {
        setModal(
            <ProductStockQuantitiesModal
                selectedModal={selectedModal}
                productId={productId}
                locationId={inventoryLocationId}
                onCloseCallback={() => setModal(<></>)}
            />
        )
    }

    const handleSave = (productId, inventoryLocationId, initialQoh, newQoh) => {
        const qoh = parseInt(newQoh)
        const definition = definitionListing.find(item => item.direction === INVENTORY_STOCK_DIRECTION.IN_OUT)

        if (initialQoh !== qoh && definition) {
            const measure = initialQoh === 0 ? qoh + parseInt(initialQoh) : qoh - parseInt(initialQoh)

            const itemAdjustment: ItemAdjustment = {
                productId: productId,
                currency: props.product.attributes.currency,
                measure: String(measure)
            }

            dispatch(ProductAction.generateQohAdjustment({
                inventoryLocationId: inventoryLocationId,
                definitionId: definition.id,
                itemAdjustment: itemAdjustment
            }))
        }
    }

    const headers = (): JSX.Element[] => {
        const matrixHeader = [
            <FormattedMessage
                key="inventory.product.quantities.product_list.header_variant_name"
                id="inventory.product.quantities.product_list.header_variant_name"
                defaultMessage="Variant Name"
            />
        ]
        const headers = [
            <FormattedMessage
                key="inventory.product.quantities.product_list.header_location_name"
                id="inventory.product.quantities.product_list.header_location_name"
                defaultMessage="Stock Location"
            />,
            <FormattedMessage
                key="inventory.product.quantities.product_list.header_quantity_available"
                id="inventory.product.quantities.product_list.header_quantity_available"
                defaultMessage="Quantity Available"
            />,
            <FormattedMessage
                key="inventory.product.quantities.product_list.header_quantity_on_hand"
                id="inventory.product.quantities.product_list.header_quantity_on_hand"
                defaultMessage="Quantity On Hand"
            />,
            <FormattedMessage
                key="inventory.product.quantities.product_list.header_quantity_reserved"
                id="inventory.product.quantities.product_list.header_quantity_reserved"
                defaultMessage="Quantity Reserved"
            />,
            <FormattedMessage
                key="inventory.product.quantities.product_list.header_quantity_in_transit"
                id="inventory.product.quantities.product_list.header_quantity_in_transit"
                defaultMessage="Quantity In Transit"
            />, <></>
        ]

        if (props.product?.attributes.type == PRODUCT_TYPE.MATRIX_PARENT) {
            return matrixHeader.concat(headers)
        }
        return headers
    }

    const openReorderModal = (invLoc: InventoryLocationQuantities) => {
        setModal(
            <>
                <ReorderPointModal
                    dataId={invLoc.id}
                    reorderPoint={invLoc.attributes.reorderPoint}
                    reorderQuantity={invLoc.attributes.reorderQuantity}
                    onCloseCallback={() => setModal(<></>)}
                    onSaveCallback={() => setModal(<></>)}
                />
            </>
        )
    }


    const getRowCellsData = (productId: string,
        productLocationQuantity: InventoryLocationQuantities | undefined, inventoryLocation: NormalizedLocation) => {
        let qa = 0
        let qoh = 0
        let qr = 0
        let qit = 0
        let initialQoh = 0

        if (productLocationQuantity) {
            qoh = parseInt(productLocationQuantity.attributes.quantityOnHand ?? 0)
            qa = parseInt(productLocationQuantity.attributes.quantityOnHand ?? 0) - parseInt(productLocationQuantity.attributes.quantityReserved ?? 0)
            qr = parseInt(productLocationQuantity.attributes.quantityReserved ?? 0)
            qit = parseInt(productLocationQuantity.attributes.quantityInTransit ?? 0)
            initialQoh = parseInt(productLocationQuantity.attributes.quantityOnHand ?? 0)
        }

        const rows = [
            <Link to={`/inventory/stock-locations/${inventoryLocation.id}/edit`}
                key={`${productLocationQuantity?.attributes.locationId}-inventoryLocationName`}>
                {inventoryLocation.name}
            </Link>,
            <>
                <FormattedNumber
                    key={`${inventoryLocation.id}-quantity-available`}
                    value={qa}
                />
            </>,
            <>
                <QuantityInputWithActions
                    id={`${inventoryLocation.id}-quantity-on-hand`}
                    name={`${inventoryLocation.id}-quantity-on-hand`}
                    value={qoh}
                    type="text"
                    required={false}
                    onSaveCallback={(newQoh) => {
                        handleSave(productId, inventoryLocation.id, initialQoh, newQoh)
                    }}
                />
            </>,
            <>
                {Number(productLocationQuantity?.attributes.quantityReserved) > 0
                    ? <a href="#" onClick={(e) => {
                        e.preventDefault()
                        onModalOpen(productId, inventoryLocation.id, INVENTORY_RESERVATION)
                    }}>
                        <FormattedNumber
                            key={`${inventoryLocation.id}-quantity-reserved`}
                            value={Number(qr)}
                        />
                    </a>
                    : <FormattedNumber
                        key={`${inventoryLocation.id}-quantity-reserved`}
                        value={Number(qr)}
                    />
                }
            </>,
            <>
                {Number(productLocationQuantity?.attributes.quantityInTransit) > 0
                    ? <a href="#" onClick={(e) => {
                        e.preventDefault()
                        onModalOpen(productId, inventoryLocation.id, INVENTORY_IN_TRANSIT)
                    }}>
                        <FormattedNumber
                            key={`${inventoryLocation.id}-quantity-in-transit`}
                            value={Number(qit)}
                        />
                    </a>
                    : <FormattedNumber
                        key={`${inventoryLocation.id}-quantity-in-transit`}
                        value={Number(qit)}
                    />
                }
            </>,
            productLocationQuantity ? <Button key="reorder-modal" variant={"outline-primary"} size={"sm"} onClick={() =>
                openReorderModal(productLocationQuantity)}>
                <Icon value={"ellipsis-h"} className={"mr-1"}/>
            </Button> : <></>
        ]


        return rows
    }

    const rows = () => {
        if (props.product?.attributes.type == PRODUCT_TYPE.MATRIX_PARENT) {
            let matrixRows: BasicRow[] = []
            props.variantIds.forEach(p => {
                const matrixColum: JSX.Element[] = [
                    <>
                        <FormattedMessage
                            id="listing_matrix_inventory_location.variant.id"
                            defaultMessage="{variantName}"
                            values={{
                                variantName: p.variantName
                            }}
                        />
                    </>,
                ]

                matrixRows = matrixRows.concat(props.locations.map(l => {
                    const productLocationQuantity = productInvLocationQuantities.find(q => {
                        if (q.attributes.locationId == l.id && q.attributes.variantId == p.variantId) {
                            return q
                        }
                    })

                    return {
                        rowId: l.id,
                        data: matrixColum.concat(getRowCellsData(productLocationQuantity?.attributes.variantId || p.variantId,
                            productLocationQuantity, l)),
                    }
                }))
            })
            return matrixRows
        }

        return props.locations.map(l => {
            const productLocationQuantity = productInvLocationQuantities.find(q => q.attributes.locationId == l.id)
            return {
                rowId: l.id,
                data: getRowCellsData(props.product.id, productLocationQuantity, l),
            }
        })
    }

    const standardList = {
        className: "row pt-3",
        headers: {
            data: headers(), classNames: []
        },
        rows: rows(),
        smallLine: true,
        pageSize: props.locations.length,
        totalItemsCount: props.locations.length,
        showSelectionSize: false,
    }

    const search = <></>


    return (
        <>
            {modal}
            <ListingWrapper
                isLoading={false}
                showHeaders={true}
                search={search}
                standardListProps={standardList}
            />
        </>
    )
}

export default injectIntl(ProductInventoryQuantitiesListing)


// WEBPACK FOOTER //
// ./src/inventory/components/ProductInventoryQuantitiesListing.tsx